import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import { useNavigate } from 'react-router';
import { useAppContext } from '../../contexts/AppContext';
import { fetchHomeDashboard } from '../../services/homeDashboardService';

export default function Offers() {
  const navigate = useNavigate();
  const { user } = useAppContext();
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const userId = useMemo(
    () => user?.user_id ?? user?.id ?? user?.customer_id ?? '',
    [user]
  );

  useEffect(() => {
    let isMounted = true;

    const loadOffers = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await fetchHomeDashboard({ userId });

        if (!isMounted) {
          return;
        }

        setOffers(response.banners || []);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setLoadError(error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadOffers();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 148px)',
        px: { xs: 1.5, sm: 2 },
        pt: 2,
        pb: 12,
      }}
    >
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              width: 46,
              height: 46,
              borderRadius: '16px',
              background: '#fff',
              boxShadow: '0 12px 24px rgba(17, 24, 39, 0.08)',
            }}
          >
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.6rem', sm: '1.9rem' } }}>
            Offers
          </Typography>
          <Box sx={{ width: 46 }} />
        </Box>

        <Paper
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: '30px',
            background:
              'linear-gradient(135deg, rgba(255,247,244,0.96) 0%, rgba(255,255,255,0.98) 100%)',
            boxShadow: '0 22px 42px rgba(17, 24, 39, 0.08)',
            mb: 2.5,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 'auto -60px -60px auto',
              width: 180,
              height: 180,
              borderRadius: '999px',
              background: 'radial-gradient(circle, rgba(255,141,120,0.25) 0%, rgba(255,141,120,0) 72%)',
            }}
          />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#ff7a67',
                mb: 0.75,
              }}
            >
              Today&apos;s spotlight
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.6rem', sm: '2rem' },
                color: '#202733',
                mb: 0.75,
              }}
            >
              Browse every live offer in one place
            </Typography>
            <Typography sx={{ color: '#7d848f', maxWidth: 620, lineHeight: 1.7 }}>
              Explore banner promotions, seasonal picks, and snack specials without
              swiping through the slider one by one.
            </Typography>
          </Box>
        </Paper>

        {loadError ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            {loadError.message}
          </Alert>
        ) : null}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gap: 2 }}>
            {offers.map((offer, index) => (
              <Paper
                key={offer.banner_id || offer.title || index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1.25fr 0.85fr' },
                  gap: 2,
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: '28px',
                  background:
                    'linear-gradient(135deg, rgba(44,34,29,0.98) 0%, rgba(106,78,66,0.92) 52%, rgba(244,227,223,0.96) 100%)',
                  color: '#fff',
                  overflow: 'hidden',
                  position: 'relative',
                  boxShadow: '0 22px 36px rgba(62, 39, 30, 0.14)',
                  animation: `routeSlideFadeIn 420ms cubic-bezier(0.22, 1, 0.36, 1) ${index * 70}ms both`,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 'auto auto -60px -40px',
                    width: 220,
                    height: 220,
                    borderRadius: '999px',
                    background:
                      'radial-gradient(circle, rgba(255,172,93,0.22) 0%, rgba(255,172,93,0) 74%)',
                  }}
                />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.2,
                      py: 0.75,
                      borderRadius: '999px',
                      background: 'rgba(255,255,255,0.14)',
                      mb: 1.5,
                    }}
                  >
                    <LocalOfferRoundedIcon sx={{ fontSize: 18, color: '#ffd36d' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.84rem' }}>
                      {offer.title || `Offer ${index + 1}`}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '1.45rem', sm: '1.8rem' },
                      lineHeight: 1.15,
                      mb: 1,
                      maxWidth: 460,
                    }}
                  >
                    {offer.subtitle || 'Fresh deals waiting for your next order'}
                  </Typography>

                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.82)',
                      lineHeight: 1.7,
                      maxWidth: 420,
                    }}
                  >
                    Hand-picked snack offers designed to make repeat orders feel easy,
                    fast, and worth coming back for.
                  </Typography>

                  <Button
                    endIcon={<ArrowOutwardRoundedIcon />}
                    sx={{
                      mt: 2.2,
                      minHeight: 50,
                      px: 2.2,
                      borderRadius: '18px',
                      textTransform: 'none',
                      fontWeight: 800,
                      background: 'linear-gradient(180deg, #ff8d78 0%, #ff5c67 100%)',
                      color: '#fff',
                      boxShadow: '0 16px 26px rgba(255, 92, 103, 0.22)',
                    }}
                    onClick={() => navigate('/')}
                  >
                    {offer.title ? `Explore ${offer.title}` : 'Explore offer'}
                  </Button>
                </Box>

                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    minHeight: { xs: 180, sm: 220 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 180, sm: 220 },
                      height: { xs: 180, sm: 220 },
                      borderRadius: '32px',
                      background:
                        'radial-gradient(circle at 35% 30%, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.06) 55%, rgba(255,255,255,0) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16)',
                    }}
                  >
                    <Box
                      component='img'
                      src={offer.image}
                      alt={offer.title || `Offer ${index + 1}`}
                      sx={{
                        width: '78%',
                        height: '78%',
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 18px 28px rgba(26, 18, 14, 0.18))',
                      }}
                    />
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
